'use client';

import type { CSSProperties, ReactNode } from 'react';

import { useNativePresentation } from '../_lib/use-native-presentation';
import { useNativeTokens } from '../_lib/use-native-tokens';
import { Eyebrow } from './eyebrow';

/**
 * Shared editorial page shell for every native (tenant) surface — packages,
 * memberships, bookings, guests, profile, membership detail, and the
 * /membership intake form. Replaces the hand-rolled outer div pattern that
 * each page used to inline.
 *
 * Centers content with a desktop max-width so a 2000px monitor doesn't
 * stretch the page edge-to-edge, while staying full-bleed on mobile (the
 * gutter is owned by `paddingInline` on the outer surface).
 *
 * Pages that need a different bottom gutter (e.g. /guests uses 48 instead of
 * 32) can override via `paddingBottom`.
 *
 * The optional `header` slot is a render escape hatch for pages whose header
 * isn't a simple eyebrow + title (e.g. membership detail's hero card). Most
 * pages just pass `eyebrow` + `title` (+ optional `subtitle`).
 */
export function NativePageShell({
  eyebrow,
  title,
  subtitle,
  header,
  children,
  maxWidth = 640,
  paddingBottom = 32,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Custom header — overrides eyebrow/title/subtitle when provided. */
  header?: ReactNode;
  children: ReactNode;
  /** Desktop content width cap. Default 640 fits the form/picker idiom. */
  maxWidth?: number;
  paddingBottom?: number;
}) {
  const t = useNativeTokens();
  const presentation = useNativePresentation();

  const surfaceStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: t.background,
    color: t.foreground,
    paddingInline: 22,
    paddingTop: 24,
    paddingBottom,
  };

  return (
    <div
      data-native-surface
      className={presentation.className}
      style={{ ...presentation.style, ...surfaceStyle }}
    >
      <div style={{ width: '100%', maxWidth }}>
        {/* Three-way header semantics:
            - header omitted (undefined) → render the default header from eyebrow/title/subtitle
            - header={<jsx />}            → render the custom header
            - header={null}               → explicitly suppress all header chrome
            Using `!== undefined` instead of `??` makes the null case explicit
            rather than relying on the default header returning null on empty props. */}
        {header !== undefined ? (
          header
        ) : (
          <NativePageHeader
            eyebrow={eyebrow}
            title={title}
            subtitle={subtitle}
          />
        )}
        {children}
      </div>
    </div>
  );
}

function NativePageHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
}) {
  const t = useNativeTokens();

  if (!eyebrow && !title && !subtitle) return null;

  return (
    <header style={{ marginBottom: 22 }}>
      {eyebrow ? (
        <Eyebrow size="md" style={{ marginBottom: 10 }}>
          {eyebrow}
        </Eyebrow>
      ) : null}
      {title ? (
        <h1
          style={{
            margin: 0,
            fontFamily: t.fontDisplay,
            fontSize: 30,
            fontWeight: 400,
            letterSpacing: '-0.015em',
            lineHeight: 1.1,
            color: t.foreground,
          }}
        >
          {title}
        </h1>
      ) : null}
      {subtitle ? (
        <p
          style={{
            margin: '8px 0 0',
            fontFamily: t.fontSans,
            fontSize: 13,
            color: t.muted,
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </header>
  );
}
